import React, { useState } from 'react';
import { useToast } from '../../../../context/ToastContext';
import { useConfirm } from '../../../../context/ConfirmContext';
import { testConnection, getAnalyticsData } from '../api';

export default function GoogleAnalyticsSettings({ connection, onUpdate }) {
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(false);
  const [testLoading, setTestLoading] = useState(false);
  const [testData, setTestData] = useState(null);
  const [settings, setSettings] = useState({
    name: connection.name,
    propertyId: connection.settings?.propertyId || ''
  });

  const handlePropertySelect = async (propertyId) => {
    const selectedProperty = connection.settings?.availableProperties?.find(
      p => p.id === propertyId
    );
    if (!selectedProperty) return;

    try {
      await onUpdate({
        ...connection,
        name: selectedProperty.name,
        settings: {
          ...connection.settings,
          propertyId: selectedProperty.id,
          propertyName: selectedProperty.name
        }
      });
      addToast('Property selected successfully', 'success');
    } catch (error) {
      addToast('Failed to update property selection', 'error');
    }
  };

  const handleResetConnection = async () => {
    const confirmed = await confirm({
      title: 'Reset Connection',
      message: 'Are you sure you want to reset this connection? You will need to reconnect with Google Analytics.',
      confirmText: 'Reset Connection',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        await onUpdate({
          ...connection,
          credentials: null,
          settings: {
            name: connection.name,
            propertyId: null
          }
        });
        addToast('Connection reset successfully', 'success');
      } catch (error) {
        addToast('Failed to reset connection', 'error');
      }
    }
  };

  const handleTestAnalytics = async () => {
    setTestLoading(true);
    setTestData(null);
    
    try {
      const data = await getAnalyticsData({
        accessToken: connection.credentials.accessToken,
        propertyId: connection.settings.propertyId
      });

      setTestData(data);
      addToast('Successfully retrieved analytics data', 'success');
    } catch (error) {
      console.error('Analytics test failed:', error);
      addToast('Failed to retrieve analytics data', 'error');
    } finally {
      setTestLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (connection.settings?.propertyId) {
        const testResult = await testConnection({
          accessToken: connection.credentials.accessToken,
          propertyId: connection.settings.propertyId
        });

        if (!testResult.success) {
          throw new Error(testResult.error || 'Connection test failed');
        }
      }

      await onUpdate({
        ...connection,
        name: settings.name.trim(),
        settings: {
          ...connection.settings,
          name: settings.name.trim()
        },
        last_tested_at: new Date().toISOString()
      });

      addToast('Settings updated successfully', 'success');
    } catch (error) {
      console.error('Error updating settings:', error);
      addToast(error.message || 'Failed to update settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Show property selection immediately after connection if no property is selected
  if (connection.credentials?.accessToken && !connection.settings?.propertyId && connection.settings?.availableProperties) {
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-medium">Select a Google Analytics Property</h3>
        <div className="grid gap-4">
          {connection.settings.availableProperties.map(property => (
            <button
              key={property.id}
              onClick={() => handlePropertySelect(property.id)}
              className="flex items-center p-4 border border-divider rounded-lg hover:border-accent-primary transition-colors text-left"
            >
              <div>
                <div className="font-medium">{property.name}</div>
                <div className="text-sm text-gray-400">Created: {new Date(property.createTime).toLocaleDateString()}</div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Google Analytics Connection</h3>
        <button
          onClick={handleResetConnection}
          className="px-3 py-1 text-sm text-red-500 hover:text-red-400 transition-colors"
        >
          Reset Connection
        </button>
      </div>

      <div className="bg-dark-300 p-4 rounded">
        <div className="font-medium">{connection.settings?.propertyName}</div>
        <div className="text-sm text-gray-400">Connected Property</div>
      </div>

      {/* Test Analytics Section */}
      <div className="space-y-4">
        <button
          onClick={handleTestAnalytics}
          disabled={testLoading || !connection.settings?.propertyId}
          className="w-full px-4 py-2 bg-accent-secondary text-white rounded hover:bg-accent-secondary/90 disabled:opacity-50 transition-colors"
        >
          {testLoading ? 'Testing Connection...' : 'Test Analytics Connection'}
        </button>

        {testData && (
          <div className="space-y-4">
            <h4 className="font-medium">Last 30 Days Analytics:</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-dark-400 p-4 rounded">
                <div className="text-sm text-gray-400">Total Users</div>
                <div className="text-2xl font-semibold">{testData.totalUsers}</div>
              </div>
              <div className="bg-dark-400 p-4 rounded">
                <div className="text-sm text-gray-400">Page Views</div>
                <div className="text-2xl font-semibold">{testData.pageViews}</div>
              </div>
              <div className="bg-dark-400 p-4 rounded">
                <div className="text-sm text-gray-400">Avg. Session Duration</div>
                <div className="text-2xl font-semibold">{testData.avgSessionDuration}s</div>
              </div>
              <div className="bg-dark-400 p-4 rounded">
                <div className="text-sm text-gray-400">Bounce Rate</div>
                <div className="text-2xl font-semibold">{testData.bounceRate}%</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Connection Name
          </label>
          <input
            type="text"
            value={settings.name}
            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
            className="w-full px-3 py-2 bg-dark-300 border border-divider rounded focus:outline-none focus:border-accent-primary"
            required
          />
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 bg-accent-primary text-white rounded hover:bg-accent-primary/90 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
} 